import { useState, useEffect, useCallback, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Eye, ShoppingCart, CheckCircle2, X, Bell, BellOff, Flame } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { cn } from "@/lib/utils";
import { useAuth } from "@/contexts/AuthContext";

// -- Types ----------------------------------------------------------------------
type EventType = "view" | "cart" | "purchase";
type FilterType = "all" | EventType;

interface ActivityEvent {
  id: string;
  type: EventType;
  userId: string | null;
  profileId: string | null;
  userName: string;
  avatarUrl: string | null;
  albumId: string | null;
  albumName: string | null;
  quantity: number | null;
  timestamp: Date;
  read: boolean;
}

// -- Config ---------------------------------------------------------------------
const EVENT_CONFIG = {
  view: {
    color: "bg-amber-500",
    glow: "shadow-[0_0_12px_rgba(245,158,11,0.6)]",
    border: "border-amber-500/30",
    bg: "bg-amber-500/10",
    textColor: "text-amber-400",
    ringColor: "border-amber-500",
    icon: Eye,
    label: "Visualizou",
    badge: "👀",
  },
  cart: {
    color: "bg-red-500",
    glow: "shadow-[0_0_12px_rgba(239,68,68,0.6)]",
    border: "border-red-500/30",
    bg: "bg-red-500/10",
    textColor: "text-red-400",
    ringColor: "border-red-500",
    icon: ShoppingCart,
    label: "Carrinho",
    badge: "🛒",
  },
  purchase: {
    color: "bg-emerald-500",
    glow: "shadow-[0_0_12px_rgba(16,185,129,0.6)]",
    border: "border-emerald-500/30",
    bg: "bg-emerald-500/10",
    textColor: "text-emerald-400",
    ringColor: "border-emerald-500",
    icon: CheckCircle2,
    label: "Comprou",
    badge: "🛍️",
  },
} as const;

function timeAgo(date: Date): string {
  const diffSec = Math.floor((Date.now() - date.getTime()) / 1000);
  if (diffSec < 60) return `${diffSec}s`;
  const diffMin = Math.floor(diffSec / 60);
  if (diffMin < 60) return `${diffMin}min`;
  const diffH = Math.floor(diffMin / 60);
  if (diffH < 24) return `${diffH}h`;
  return `${Math.floor(diffH / 24)}d`;
}

function generateId(): string {
  return Math.random().toString(36).slice(2) + Date.now().toString(36);
}

// -- Component ------------------------------------------------------------------
export const AdminLiveActivityFeed = () => {
  const { isAdmin } = useAuth();
  const [events, setEvents] = useState<ActivityEvent[]>([]);
  const [isExpanded, setIsExpanded] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [filter, setFilter] = useState<FilterType>("all");
  const [unreadCount, setUnreadCount] = useState(0);
  const [activeRecoveryUsers, setActiveRecoveryUsers] = useState<Record<string, boolean>>({});
  const albumCacheRef = useRef<Record<string, string>>({});
  const profileCacheRef = useRef<Record<string, { name: string; avatar: string | null }>>({});

  const getProfile = useCallback(async (profileId: string | null, userId: string | null) => {
    const key = profileId || userId || "anon";
    if (profileCacheRef.current[key]) return profileCacheRef.current[key];
    let query = supabase.from("profiles").select("full_name, avatar_url");
    if (profileId) query = (query as any).eq("id", profileId);
    else if (userId) query = (query as any).eq("user_id", userId);
    else return { name: "Visitante", avatar: null };
    const { data } = await (query as any).single();
    const result = { name: data?.full_name || "Usuário", avatar: data?.avatar_url || null };
    profileCacheRef.current[key] = result;
    return result;
  }, []);

  const getAlbumName = useCallback(async (albumId: string | null) => {
    if (!albumId) return null;
    if (albumCacheRef.current[albumId]) return albumCacheRef.current[albumId];
    const { data } = await supabase.from("albums").select("name").eq("id", albumId).single();
    const name = data?.name || null;
    if (name) albumCacheRef.current[albumId] = name;
    return name;
  }, []);

  const pushEvent = useCallback((event: ActivityEvent) => {
    setEvents((prev) => {
      const duplicate = prev.some(
        (e) => e.type === event.type &&
          e.profileId === event.profileId &&
          e.userId === event.userId &&
          Math.abs(new Date(e.timestamp).getTime() - event.timestamp.getTime()) < 5000
      );
      if (duplicate) return prev;
      return [event, ...prev].slice(0, 50);
    });
    if (!isMuted) setUnreadCount((c) => c + 1);
  }, [isMuted]);

  useEffect(() => {
    if (!isAdmin) return;

    const fetchHistory = async () => {
      try {
        const since = new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString();
        const history: ActivityEvent[] = [];

        const { data: recentViews } = await supabase
          .from("album_views")
          .select("id, album_id, user_id, created_at")
          .gte("created_at", since)
          .order("created_at", { ascending: false })
          .limit(20);

        for (const v of recentViews || []) {
          const p = await getProfile(v.user_id, null);
          const albumName = await getAlbumName(v.album_id);
          history.push({ id: v.id || generateId(), type: "view", userId: null, profileId: v.user_id, userName: p.name, avatarUrl: p.avatar, albumId: v.album_id, albumName, quantity: null, timestamp: new Date(v.created_at), read: true });
        }

        const { data: recentOrders } = await supabase
          .from("orders")
          .select("id, user_id, status, created_at, updated_at")
          .gte("created_at", since)
          .in("status", ["pending", "approved", "paid", "completed"])
          .order("created_at", { ascending: false })
          .limit(20);

        for (const o of recentOrders || []) {
          const p = await getProfile(null, o.user_id);
          const type: EventType = ["approved", "paid", "completed"].includes(o.status) ? "purchase" : "cart";
          history.push({ id: o.id || generateId(), type, userId: o.user_id, profileId: null, userName: p.name, avatarUrl: p.avatar, albumId: null, albumName: null, quantity: null, timestamp: new Date(o.created_at), read: true });
        }

        history.sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime());
        setEvents(history.slice(0, 50));
      } catch (e) {
        console.warn("LiveActivityFeed: history fetch failed", e);
      }
    };

    fetchHistory();

    const viewsChannel = supabase
      .channel("live_feed_views")
      .on("postgres_changes", { event: "INSERT", schema: "public", table: "album_views" }, async (payload) => {
        const row = payload.new as any;
        const p = await getProfile(row.user_id, null);
        const albumName = await getAlbumName(row.album_id);
        pushEvent({ id: row.id || generateId(), type: "view", userId: null, profileId: row.user_id, userName: p.name, avatarUrl: p.avatar, albumId: row.album_id, albumName, quantity: null, timestamp: new Date(row.created_at || Date.now()), read: false });
      })
      .subscribe();

    const ordersChannel = supabase
      .channel("live_feed_orders")
      .on("postgres_changes", { event: "INSERT", schema: "public", table: "orders" }, async (payload) => {
        const row = payload.new as any;
        if (row.status !== "pending") return;
        const p = await getProfile(null, row.user_id);
        pushEvent({ id: row.id || generateId(), type: "cart", userId: row.user_id, profileId: null, userName: p.name, avatarUrl: p.avatar, albumId: null, albumName: null, quantity: null, timestamp: new Date(row.created_at || Date.now()), read: false });
      })
      .on("postgres_changes", { event: "UPDATE", schema: "public", table: "orders" }, async (payload) => {
        const row = payload.new as any;
        if (!["approved", "paid", "completed"].includes(row.status)) return;
        const p = await getProfile(null, row.user_id);
        let quantity: number | null = null;
        try { const { count } = await supabase.from("order_items").select("*", { count: "exact", head: true }).eq("order_id", row.id); quantity = count; } catch {}
        pushEvent({ id: (row.id || generateId()) + "-purchase", type: "purchase", userId: row.user_id, profileId: null, userName: p.name, avatarUrl: p.avatar, albumId: null, albumName: null, quantity, timestamp: new Date(row.updated_at || Date.now()), read: false });
      })
      .subscribe();

    return () => {
      supabase.removeChannel(viewsChannel);
      supabase.removeChannel(ordersChannel);
    };
  }, [isAdmin, getProfile, getAlbumName, pushEvent]);

  if (!isAdmin) return null;

  const filteredEvents = filter === "all" ? events : events.filter((e) => e.type === filter);
  const uniqueAvatarEvents: ActivityEvent[] = []; const seenNames = new Set(); for (const e of events) { if (!seenNames.has(e.userName)) { seenNames.add(e.userName); uniqueAvatarEvents.push(e); } } const latestAvatarEvents = uniqueAvatarEvents.slice(0, 7);

  return (
    <>
      {/* COLLAPSED MODE */}
      <AnimatePresence>
        {!isExpanded && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed top-[15%] right-4 z-[9998] flex flex-col gap-2 items-end pointer-events-none"
          >
            {/* Bell button */}
            <motion.button
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => { setIsExpanded(true); setUnreadCount(0); setEvents(prev => prev.map(e => ({ ...e, read: true }))); }}
              className="pointer-events-auto relative w-11 h-11 rounded-full bg-[#0f0f0f]/90 border border-white/10 backdrop-blur-xl flex items-center justify-center text-white/50 hover:text-white shadow-2xl hover:border-white/20 transition-all"
            >
              <Bell className="w-4 h-4" />
              {unreadCount > 0 && (
                <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 bg-red-500 rounded-full text-[9px] font-black text-white flex items-center justify-center animate-pulse">
                  {unreadCount > 9 ? "9+" : unreadCount}
                </span>
              )}
            </motion.button>

            {/* Avatar stack */}
            <div className="flex flex-col gap-2 pointer-events-auto">
              <AnimatePresence mode="popLayout">
                {latestAvatarEvents.map((event, index) => {
                  const cfg = EVENT_CONFIG[event.type];
                  const IconComp = cfg.icon;
                  return (
                    <motion.div
                      key={event.id}
                      layout
                      initial={{ opacity: 0, x: 40, scale: 0.7 }}
                      animate={{ opacity: 1, x: 0, scale: 1 }}
                      exit={{ opacity: 0, scale: 0.4, x: 40 }}
                      transition={{ type: "spring", stiffness: 400, damping: 28, delay: index * 0.04 }}
                      className="group relative flex items-center justify-end"
                    >
                      {/* Hover tooltip */}
                      <div className="absolute right-[calc(100%+12px)] opacity-0 group-hover:opacity-100 pointer-events-none transition-all duration-200 whitespace-nowrap z-50 min-w-[180px]">
                        <div className={cn("px-3 py-2.5 rounded-2xl text-[10px] border backdrop-blur-xl shadow-2xl", cfg.bg, cfg.border)}>
                          <p className={cn("font-black uppercase tracking-widest flex items-center gap-1.5 mb-1", cfg.textColor)}>
                            <IconComp className="w-3 h-3" />
                            {event.type === "view" ? "Apenas visualizou" : event.type === "cart" ? "Carrinho abandonado" : `Comprou ${event.quantity ? `${event.quantity} foto${event.quantity > 1 ? "s" : ""}` : ""}`}
                          </p>
                          <p className="text-white/80 font-bold truncate">{event.userName}</p>
                          {event.albumName && <p className="text-white/50 truncate">📁 {event.albumName}</p>}
                          <p className="text-white/30 mt-1">🕒 {timeAgo(event.timestamp)} atrás</p>
                        </div>
                      </div>

                      {/* Avatar */}
                      <div className={cn(
                        "relative w-11 h-11 transition-all duration-300 group-hover:scale-125 group-hover:-translate-x-1"
                      )}>
                        <div className={cn("w-full h-full rounded-full overflow-hidden border-2", cfg.ringColor, !event.read && cfg.glow)}>
                          {event.avatarUrl ? (
                            <img src={event.avatarUrl} alt={event.userName} className="w-full h-full object-cover" />
                          ) : (
                            <div className={cn("w-full h-full flex items-center justify-center text-sm font-black text-white/60 bg-white/5")}>
                              {event.userName?.[0]?.toUpperCase() || "?"}
                            </div>
                          )}
                                                </div>
                        {new Date().getTime() - event.timestamp.getTime() < 3 * 60 * 1000 && (
                          <span className="absolute -bottom-1 -right-1 w-3.5 h-3.5 bg-green-500 rounded-full border-2 border-[#0a0a0a] shadow-[0_0_8px_rgba(34,197,94,0.8)] z-10 animate-pulse" />
                        )}
                        {event.userId && activeRecoveryUsers[event.userId] && (
                          <div className="absolute -top-1.5 -left-1.5 w-5 h-5 bg-gradient-to-br from-[#FF0055] to-[#FF6A00] rounded-full flex items-center justify-center border-2 border-[#0a0a0a] shadow-[0_0_10px_rgba(255,106,0,0.8)] z-20 animate-bounce">
                            <Flame className="w-2.5 h-2.5 text-white" />
                          </div>
                        )}
                      </div>
                    </motion.div>
                  );
                })}
              </AnimatePresence>
            </div>

            {events.length === 0 && (
              <div className="w-11 h-11 rounded-full bg-white/5 border border-white/5 flex items-center justify-center">
                <Eye className="w-4 h-4 text-white/15" />
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>

      {/* EXPANDED MODE */}
      <AnimatePresence>
        {isExpanded && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[9999] bg-black/70 backdrop-blur-md flex items-stretch justify-end"
            onClick={(e) => { if (e.target === e.currentTarget) setIsExpanded(false); }}
          >
            <motion.div
              initial={{ x: "100%", opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              exit={{ x: "100%", opacity: 0 }}
              transition={{ type: "spring", stiffness: 380, damping: 35 }}
              className="w-full max-w-sm h-full bg-[#080808] border-l border-white/10 flex flex-col shadow-[-30px_0_80px_rgba(0,0,0,0.9)]"
            >
              {/* Header */}
              <div className="flex items-center justify-between px-5 py-4 border-b border-white/8">
                <div>
                  <h2 className="text-sm font-black text-white uppercase tracking-[0.15em] flex items-center gap-2">
                    <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse shadow-[0_0_8px_rgba(16,185,129,0.8)]" />
                    Atividade ao Vivo
                  </h2>
                  <p className="text-[10px] text-white/30 mt-0.5 font-medium">{events.length} eventos nas últimas 24h</p>
                </div>
                <div className="flex items-center gap-2">
                  <button onClick={() => setIsMuted(m => !m)} title={isMuted ? "Ativar notificações" : "Silenciar"} className={cn("w-8 h-8 rounded-xl flex items-center justify-center border transition-all", isMuted ? "bg-red-500/20 border-red-500/30 text-red-400" : "bg-white/5 border-white/10 text-white/40 hover:text-white/70")}>
                    {isMuted ? <BellOff className="w-3.5 h-3.5" /> : <Bell className="w-3.5 h-3.5" />}
                  </button>
                  <button onClick={() => setIsExpanded(false)} className="w-8 h-8 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center text-white/40 hover:text-white hover:bg-white/10 transition-all">
                    <X className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>

              {/* Filters */}
              <div className="flex gap-1.5 px-4 py-3 border-b border-white/5">
                {(["all", "view", "cart", "purchase"] as FilterType[]).map((f) => {
                  const label = f === "all" ? "Todos" : EVENT_CONFIG[f as EventType].label;
                  const cfg = f !== "all" ? EVENT_CONFIG[f as EventType] : null;
                  const count = f === "all" ? events.length : events.filter(e => e.type === f).length;
                  return (
                    <button
                      key={f}
                      onClick={() => setFilter(f)}
                      className={cn(
                        "flex-1 py-1.5 px-1 rounded-xl text-[9px] font-black uppercase tracking-widest transition-all border flex flex-col items-center gap-0.5",
                        filter === f
                          ? f === "all" ? "bg-white/10 border-white/20 text-white" : cn(cfg!.bg, cfg!.border, cfg!.textColor)
                          : "bg-transparent border-white/5 text-white/25 hover:text-white/40"
                      )}
                    >
                      {f !== "all" && <span className="text-base leading-none">{EVENT_CONFIG[f as EventType].badge}</span>}
                      <span>{label}</span>
                      <span className="opacity-60">({count})</span>
                    </button>
                  );
                })}
              </div>

              {/* Events list */}
              <div className="flex-1 overflow-y-auto px-4 py-3 space-y-2" style={{ scrollbarWidth: "thin", scrollbarColor: "rgba(255,255,255,0.1) transparent" }}>
                <AnimatePresence mode="popLayout">
                  {filteredEvents.length === 0 ? (
                    <div className="flex flex-col items-center justify-center h-full text-center py-12">
                      <Eye className="w-10 h-10 text-white/8 mb-3" />
                      <p className="text-[11px] text-white/25 font-black uppercase tracking-widest">Nenhuma atividade</p>
                      <p className="text-[10px] text-white/15 mt-1">aguardando eventos em tempo real...</p>
                    </div>
                  ) : (
                    filteredEvents.map((event) => {
                      const cfg = EVENT_CONFIG[event.type];
                      const IconComp = cfg.icon;
                      let description = "";
                      if (event.type === "view") description = event.albumName ? `Acessou o álbum "${event.albumName}"` : "Acessou um álbum";
                        else if (event.type === "cart") {
                          const diffMs = Date.now() - event.timestamp.getTime();
                          const diffMinutes = diffMs / (1000 * 60);
                          const diffHours = diffMs / (1000 * 60 * 60);
                          
                          let recoveryStatus = "";
                          if (diffMinutes < 60) {
                            recoveryStatus = `⏱️ ${Math.ceil(60 - diffMinutes)}m p/ Lembrete`;
                          } else if (diffHours < 9) {
                            recoveryStatus = `⏳ ${Math.ceil(9 - diffHours)}h p/ Oferta 50% Popup`;
                          } else {
                            recoveryStatus = `✅ Oferta popup disponível`;
                          }
                          
                          description = `Chegou ao carrinho mas não finalizou • ${recoveryStatus}`;
                        }
                      else description = event.quantity ? `Comprou ${event.quantity} foto${event.quantity > 1 ? "s" : ""}` : "Concluiu uma compra";

                      return (
                        <motion.div
                          key={event.id}
                          layout
                          initial={{ opacity: 0, y: -10, scale: 0.95 }}
                          animate={{ opacity: 1, y: 0, scale: 1 }}
                          exit={{ opacity: 0, height: 0, marginBottom: 0 }}
                          className={cn("flex items-center gap-3 p-3 rounded-2xl border transition-all", cfg.bg, cfg.border, !event.read && cfg.glow)}
                        >
                          <div className={cn("relative w-10 h-10 rounded-full overflow-hidden border-2 shrink-0", cfg.ringColor)}>
                            {event.avatarUrl ? (
                              <img src={event.avatarUrl} alt={event.userName} className="w-full h-full object-cover" />
                            ) : (
                              <div className="w-full h-full bg-white/5 flex items-center justify-center text-sm font-black text-white/50">
                                {event.userName?.[0]?.toUpperCase() || "?"}
                                                            </div>
                            )}
                          </div>
                          {event.userId && activeRecoveryUsers[event.userId] && (
                            <div className="absolute -top-1.5 -left-1.5 w-4 h-4 bg-gradient-to-br from-[#FF0055] to-[#FF6A00] rounded-full flex items-center justify-center shadow-[0_0_8px_rgba(255,106,0,0.8)] z-20" title="Oferta Ativa">
                              <Flame className="w-2 h-2 text-white" />
                            </div>
                          )}
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-1.5 flex-wrap">
                              <p className="text-[11px] font-black text-white truncate max-w-[110px]">{event.userName}</p>
                              <span className={cn("inline-flex items-center gap-1 px-1.5 py-0.5 rounded-lg text-[8px] font-black uppercase tracking-widest", cfg.bg, cfg.textColor)}>
                                <IconComp className="w-2.5 h-2.5" />
                                {cfg.label}
                              </span>
                            </div>
                            <p className="text-[10px] text-white/50 truncate mt-0.5">{description}</p>
                          </div>
                          <p className="text-[9px] text-white/25 shrink-0 font-bold">{timeAgo(event.timestamp)}</p>
                        </motion.div>
                      );
                    })
                  )}
                </AnimatePresence>
              </div>

              {/* Footer legend */}
              <div className="px-4 py-3 border-t border-white/5 flex justify-between items-center">
                <div className="flex items-center gap-3">
                  {(["view", "cart", "purchase"] as EventType[]).map((t) => (
                    <span key={t} className={cn("flex items-center gap-1 text-[8px] font-bold uppercase tracking-widest", EVENT_CONFIG[t].textColor)}>
                      <span className={cn("w-2 h-2 rounded-full", EVENT_CONFIG[t].color)} />
                      {EVENT_CONFIG[t].label}
                    </span>
                  ))}
                </div>
                <button onClick={() => setEvents([])} className="text-[9px] text-white/20 hover:text-red-400 transition-colors font-bold uppercase tracking-widest">
                  Limpar
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};



